import cookie from 'js-cookie';
import api from '../../utils/service/api';
import * as actionType from '../../utils/constants/actions';
import {
  CONFIG_SERVER,
  routes,
  REFRESH_TOKEN,
  TOKEN,
  BASE_ROUTER_PREFIX,
  VINLAB_LOCALE,
  USER_ROLES,
  FIRST_REFRESH_TOKEN,
  ROLES,
  VINLAB_VIEW_MODE,
} from '../../utils/constants/config';

const {
  CLIENT_ID,
  LOGIN_CALLBACK_URI,
  OIDC_LOGOUT_URI,
  OIDC_USERINFO_ENDPOINT,
  OIDC_ACCESS_TOKEN_URI,
  RESPONSE_TYPE,
  STATE,
  OIDC_AUTHORIZATION_URI,
  BASE_URL,
  SCOPE,
  AUDIENCE,
  TOKEN_PERMISSION,
} = CONFIG_SERVER;

const ENDPOINT_KEYCLOAK = '/auth/realms/vinlab/protocol/openid-connect';

export const actionChangeLanguage = (lang) => {
  cookie.set(VINLAB_LOCALE, lang);
  return {
    type: actionType.CHANGE_LANGUAGE,
    payload: lang,
  };
};
export const actionShowLoading = () => ({ type: actionType.SHOW_LOADING });

export const actionHideLoading = () => ({ type: actionType.HIDE_LOADING });

export const getAccountInfo = () => async (dispatch) => {
  try {
    dispatch(actionShowLoading());
    const { data } = await api({
      method: 'get',
      url:
        OIDC_USERINFO_ENDPOINT || `${BASE_URL + ENDPOINT_KEYCLOAK}/userinfo`,
    });

    // Get roles from API's permissions endpoint
    // Fall back chain: realm_access.roles (standard JWT claim) → realm_roles (custom mapper)
    let realm_roles = data?.realm_access?.roles || data?.realm_roles || [];
    try {
      const permissionsRes = await api({
        method: 'get',
        url: '/api/accounts/permissions',
      });
      // API returns nested structure: { data: { data: [...], system_roles: [...] } }
      // Only overwrite if the API actually returned non-empty roles (avoid clearing
      // roles when the API receives a UMA RPT that strips the custom claim).
      const apiData = permissionsRes?.data?.data;
      const apiRoles = apiData?.system_roles;
      if (Array.isArray(apiRoles) && apiRoles.length > 0) {
        realm_roles = apiRoles;
      }
      // Inject hospital_id and group_id from the API response — more reliable
      // than the Keycloak userinfo endpoint which may omit custom claims.
      if (apiData?.hospital_id) data.hospital_id = apiData.hospital_id;
      if (apiData?.group_id)    data.group_id    = apiData.group_id;
    } catch (err) {
      console.warn('Failed to fetch permissions, using roles from token', err);
    }

    // Extract roles and map to realm_roles
    const profileData = {
      ...data,
      realm_roles: realm_roles,
    };

    dispatch({ type: actionType.FETCHING_PROFILE, payload: profileData });

    let isValidPage = false;
    const pathname = window.location.pathname || '';
    Object.keys(routes).forEach((key) => {
      if (!isValidPage && pathname.indexOf(routes[key]) > -1) {
        isValidPage = true;
      }
    });

    if (!isValidPage) {
      window.location.replace(
        BASE_ROUTER_PREFIX + getDefaultLandingRoute(profileData)
      );
    }

    dispatch(actionHideLoading());
  } catch (error) {
    console.log(error);
    dispatch(actionHideLoading());
    actionLogout();
  }
};

export const actionGetToken = (code = '') => {
  let requestBody = new URLSearchParams();
  requestBody.append('grant_type', 'authorization_code');
  requestBody.append('client_id', CLIENT_ID);
  requestBody.append('code', code);
  requestBody.append('redirect_uri', LOGIN_CALLBACK_URI);

  return api({
    method: 'post',
    url: OIDC_ACCESS_TOKEN_URI || `${BASE_URL + ENDPOINT_KEYCLOAK}/token`,
    data: requestBody,
  });
};

export const actionGetPermissionToken = (token, listPermission) => {
  let requestBody = new URLSearchParams();
  requestBody.append(
    'grant_type',
    'urn:ietf:params:oauth:grant-type:uma-ticket'
  );
  requestBody.append('audience', AUDIENCE);

  (listPermission || TOKEN_PERMISSION).forEach((it) => {
    requestBody.append('permission', it);
  });

  return api({
    method: 'post',
    url: OIDC_ACCESS_TOKEN_URI || `${BASE_URL + ENDPOINT_KEYCLOAK}/token`,
    data: requestBody,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const actionRefreshToken = (refreshToken = '') => {
  let requestBody = new URLSearchParams();
  requestBody.append('grant_type', 'refresh_token');
  requestBody.append('client_id', CLIENT_ID);
  requestBody.append('refresh_token', refreshToken);
  requestBody.append('redirect_uri', LOGIN_CALLBACK_URI);

  return api({
    method: 'post',
    url: OIDC_ACCESS_TOKEN_URI || `${BASE_URL + ENDPOINT_KEYCLOAK}/token`,
    data: requestBody,
  });
};

export const requestLogin = () => {
  const pathAuth =
    OIDC_AUTHORIZATION_URI || `${BASE_URL + ENDPOINT_KEYCLOAK}/auth`;
  let loginUrl =
    pathAuth +
    '?client_id=' +
    CLIENT_ID +
    '&response_type=' +
    RESPONSE_TYPE +
    '&state=' +
    STATE +
    '&scope=' +
    SCOPE +
    '&redirect_uri=' +
    LOGIN_CALLBACK_URI;

  window.location.href = encodeURI(loginUrl);
};

export const actionLogout = async () => {
  try {
    localStorage.removeItem(VINLAB_VIEW_MODE);
    if (cookie.get(REFRESH_TOKEN) || cookie.get(FIRST_REFRESH_TOKEN)) {
      let requestBody = new URLSearchParams();
      requestBody.append('client_id', CLIENT_ID);
      requestBody.append('redirect_uri', LOGIN_CALLBACK_URI);
      requestBody.append(
        'refresh_token',
        cookie.get(REFRESH_TOKEN) || cookie.get(FIRST_REFRESH_TOKEN)
      );
      await api({
        method: 'post',
        url: OIDC_LOGOUT_URI || `${BASE_URL + ENDPOINT_KEYCLOAK}/logout`,
        data: requestBody,
      });
    }
    cookie.remove(TOKEN);
    cookie.remove(REFRESH_TOKEN);
    requestLogin();
  } catch (error) {
    cookie.remove(TOKEN);
    cookie.remove(REFRESH_TOKEN);
    requestLogin();
  }
};

export const checkRole = (profile, role = '') => {
  if (!profile || !role) return false;
  const { realm_roles = [] } = profile;
  return realm_roles.indexOf(role) > -1;
};

export const hasRolePO = (profile) => {
  return checkRole(profile, ROLES.PO) || checkRole(profile, ROLES.PO_PARTNER);
};

// Mirror LeftMenu's role detection so the default landing route stays in
// sync with which nav items the user can actually see.
const RADIOLOGIST_ROLES = ['ANNOTATOR', 'REVIEWER', 'LABELER', 'Labeler'];

export const isRadiologist = (profile) => {
  if (!profile) return false;
  const roles = profile.realm_roles || [];
  return Array.isArray(roles) && roles.some((r) => RADIOLOGIST_ROLES.includes(r));
};

// Role-aware default landing route used both on fresh login (full-page
// redirect from getAccountInfo) and on the in-app `/` route. POs land on
// the Admin Dashboard, radiologists on My Worklist, everyone else falls
// back to My Projects (the historical default).
export const getDefaultLandingRoute = (profile) => {
  // Teleradiology users always land on the teleradiology page regardless of role.
  if (profile?.hospital_id === 'teleradiology') return routes.TELERADIOLOGY;
  if (hasRolePO(profile)) return routes.ADMIN_DASHBOARD;
  if (isRadiologist(profile)) return routes.WORKLIST;
  // Referring physicians (read-only) land on the studies list — their only page.
  if ((profile?.realm_roles || []).includes('REFERRING_PHYSICIAN')) return routes.STUDIES;
  return routes.PROJECTS;
};

export const actionChangeViewMode = (viewmode) => async (dispatch) => {
  try {
    localStorage.setItem(VINLAB_VIEW_MODE, viewmode);
    dispatch({ type: actionType.CHANGE_VIEW_MODE, payload: viewmode });
  } catch (error) {}
};

export const isProjectOwner = (projectInfo, userInfo) => {
  if (!projectInfo || !userInfo) return false;
  const { people = [] } = projectInfo;
  const listPO = people.filter(
    (it) =>
      it.id === userInfo.sub &&
      (it?.roles || []).indexOf(USER_ROLES.PROJECT_OWNER) > -1
  );
  return (listPO || []).length > 0;
};

export const actionGetUsers = (params = {}) => async (dispatch) => {
  try {
    dispatch({ type: actionType.FETCHING_USERS, payload: true });
    const { data } = await api({
      method: 'get',
      url: '/api/accounts/userinfo',
      params,
    });
    dispatch({ type: actionType.FETCH_USERS, payload: data });
  } catch (error) {
    dispatch({ type: actionType.FETCHING_USERS, payload: false });
  }
};

export const actionGetListPermission = (token = '') => {
  return api({
    method: 'get',
    url: '/api/accounts/permissions',
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const actionShowUploadModal = (uploadInfo = {}) => {
  return {
    type: actionType.SHOW_UPLOAD_MODAL,
    payload: uploadInfo,
  };
};
